import {
  type DoctorSubstituteDocumentApi,
  type DoctorSubstituteDocumentType,
  doctorSubstituteDocumentFileSchema,
} from '@omecare/utils';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import useReplaceDoctorSubstituteDocumentMutation from '@/hooks/mutations/use-replace-doctor-substitute-document';
import { useOpenDoctorSubstituteDocument } from '@/hooks/queries/use-get-doctor-substitute-document-url';
import { openPendingDocumentTab } from '@/lib/pending-document-tab';

/** One supporting document as the card renders it. */
export type DoctorSubstituteDocumentItem = {
  type: DoctorSubstituteDocumentType;
  document: DoctorSubstituteDocumentApi;
};

/**
 * Lists the documents to display: the RCP certificate for everyone, the
 * licence de remplacement only for a non-thésé substitute.
 *
 * @param params.thesis - whether the substitute has defended their thesis
 * @param params.licenceDocument - the stored licence, or `null`
 * @param params.rcpDocument - the stored RCP certificate, or `null`
 * @returns the documents in display order
 */
export const listDoctorSubstituteDocuments = ({
  thesis,
  licenceDocument,
  rcpDocument,
}: {
  thesis: boolean;
  licenceDocument: DoctorSubstituteDocumentApi;
  rcpDocument: DoctorSubstituteDocumentApi;
}): DoctorSubstituteDocumentItem[] => {
  const items: DoctorSubstituteDocumentItem[] = [
    { type: 'rcp', document: rcpDocument },
  ];
  if (!thesis) {
    items.push({ type: 'licence', document: licenceDocument });
  }
  return items;
};

/**
 * View-model of the documents card: validates a chosen file client-side,
 * uploads it, and opens a document through its presigned URL.
 *
 * @param params.doctorSubstituteId - owner of the documents
 * @returns the card state and API
 */
export const useDoctorSubstituteDocumentsViewModel = ({
  doctorSubstituteId,
}: {
  doctorSubstituteId: string;
}) => {
  const { t } = useTranslation();
  const { mutate: replaceDocument, isPending: replacing } =
    useReplaceDoctorSubstituteDocumentMutation();
  const { open, isOpening } = useOpenDoctorSubstituteDocument();

  const replace = ({
    type,
    file,
  }: {
    type: DoctorSubstituteDocumentType;
    file: File;
  }) => {
    const validation = doctorSubstituteDocumentFileSchema.safeParse(file);
    if (!validation.success) {
      const message = validation.error.issues[0]?.message;
      toast.error(t(message ?? 'doctor-substitute-documents.replace.failed'));
      return;
    }
    replaceDocument({ doctorSubstituteId, type, file });
  };

  // Runs in the click handler: the tab must be opened before the request so
  // browsers that expire the user activation (Safari, mobile) do not block it.
  const view = (type: DoctorSubstituteDocumentType) => {
    open({ doctorSubstituteId, type, tab: openPendingDocumentTab() });
  };

  return {
    busy: replacing || isOpening,
    api: { replace, view },
  };
};

export type DoctorSubstituteDocumentsViewModel = ReturnType<
  typeof useDoctorSubstituteDocumentsViewModel
>;
